/**
 * The modeller's own route tree, rendered full-bleed.
 *
 * It sits BESIDE the fleet layout rather than inside it. The 220px rail is
 * this product's navigation, and nesting it under the fleet topbar gives one
 * page two chromes, two brands and two nav bars.
 *
 * What the two surfaces share is infrastructure, not identity: the request
 * client, the bearer token, `ApiError`. Nothing visual crosses.
 */

import type { ReactNode } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { canWrite } from '../api/client';
import { PageHeader } from './shell/Shell';
import { Empty } from './shell/states';
import { Shell } from './shell/Shell';
import { Productions } from './screens/Productions';
import { ProductionDetail } from './screens/ProductionDetail';
import { EquipmentLibrary } from './screens/EquipmentLibrary';
import { EstimateSummary } from './screens/EstimateSummary';
import { CallSheetReview } from './screens/CallSheetReview';
import { Documents } from './screens/Documents';
import { NewProduction } from './screens/NewProduction';
import { NewShootDay } from './screens/NewShootDay';
import { NewDocument } from './screens/NewDocument';

/**
 * A write screen reached without an editing key.
 *
 * Not an error: the person is signed in and everything they can do still
 * works. It says who to ask and gets out of the way.
 */
function RequiresEditing({ title, children }: { title: string; children: ReactNode }) {
  if (canWrite()) return <>{children}</>;
  return (
    <>
      <PageHeader title={title} />
      <Empty
        title="An editing key is needed for this"
        body={
          'Your access key opens the modeller for viewing. Creating or ' +
          'changing anything needs a second key, issued by Equoia. Ask for ' +
          'one, then sign in again and add it.'
        }
        action={<Link className="pem-link" to="/modeller/productions">← Productions</Link>}
      />
    </>
  );
}

export function ModellerRoutes() {
  return (
    <Shell>
      <Routes>
        <Route path="/modeller" element={<Navigate to="/modeller/productions" replace />} />
        <Route path="/modeller/productions" element={<Productions />} />
        {/* Before :slug, or "new" is read as a production handle. */}
        <Route path="/modeller/productions/new"
               element={<RequiresEditing title="Start a production"><NewProduction /></RequiresEditing>} />
        <Route path="/modeller/productions/:slug" element={<ProductionDetail />} />
        <Route path="/modeller/productions/:slug/days/new"
               element={<RequiresEditing title="Add a shooting day"><NewShootDay /></RequiresEditing>} />
        <Route path="/modeller/productions/:slug/documents" element={<Documents />} />
        <Route path="/modeller/productions/:slug/documents/new"
               element={<RequiresEditing title="Register a document"><NewDocument /></RequiresEditing>} />
        <Route path="/modeller/productions/:slug/estimates/:id" element={<EstimateSummary />} />
        {/* Scoped to the production, because the review screen needs that
            production's unit-days to offer a confirmation target. The old
            bare `/modeller/extractions/:id` could not name one. */}
        <Route path="/modeller/productions/:slug/extractions/:id" element={<CallSheetReview />} />
        <Route path="/modeller/equipment" element={<EquipmentLibrary />} />
        <Route path="*" element={<Navigate to="/modeller/productions" replace />} />
      </Routes>
    </Shell>
  );
}
